import React, { useEffect, useRef, useState } from "react";
import { Loader2, Wand2, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Highlight a word/phrase inside a section body and click the button to get
 * 3 AI alternatives. Clicking an alternative replaces the highlighted text.
 */
export const WordSwap = ({ scriptId, section, currentText, onReplace }) => {
    const [phrase, setPhrase] = useState("");
    const [sentence, setSentence] = useState("");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const anchorRef = useRef(null);

    const captureSelection = () => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return "";
        const range = sel.getRangeAt(0);
        // Ensure selection is inside this section
        const container = anchorRef.current;
        if (!container || !container.contains(range.commonAncestorContainer)) return "";
        return sel.toString().trim();
    };

    const findSentenceFor = (phrase) => {
        if (!phrase || !currentText) return phrase;
        const idx = currentText.indexOf(phrase);
        if (idx < 0) return phrase;
        // Walk back to previous sentence terminator or start
        const before = currentText.slice(0, idx);
        const startIdx = Math.max(
            before.lastIndexOf(". ") + 2,
            before.lastIndexOf("! ") + 2,
            before.lastIndexOf("? ") + 2,
            before.lastIndexOf("\n") + 1,
            0
        );
        const after = currentText.slice(idx);
        let endIdx = after.length;
        for (const t of [". ", "! ", "? ", "\n"]) {
            const p = after.indexOf(t);
            if (p >= 0 && p < endIdx) endIdx = p + 1;
        }
        return currentText.slice(startIdx, idx + endIdx).trim();
    };

    const requestSwap = async () => {
        const selected = captureSelection();
        if (!selected) {
            toast.error("Highlight a word or phrase first");
            return;
        }
        if (selected.length > 200) {
            toast.error("Highlight is too long — pick a word or short phrase");
            return;
        }
        setPhrase(selected);
        setSentence(findSentenceFor(selected));
        setOpen(true);
        setOptions([]);
        setLoading(true);
        try {
            const { data } = await api.post(`/scripts/${scriptId}/word-swap`, {
                phrase: selected,
                sentence: findSentenceFor(selected),
            });
            setOptions(data.alternatives || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Word swap failed");
            setOpen(false);
        } finally {
            setLoading(false);
        }
    };

    const applyReplacement = (opt) => {
        if (!phrase || !opt) return;
        // Replace first occurrence
        const idx = currentText.indexOf(phrase);
        if (idx < 0) {
            toast.error("Original phrase no longer in text");
            setOpen(false);
            return;
        }
        const newText = currentText.slice(0, idx) + opt + currentText.slice(idx + phrase.length);
        onReplace(newText);
        toast.success("Swapped");
        setOpen(false);
    };

    // Close on Escape
    useEffect(() => {
        const h = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, []);

    return (
        <div ref={anchorRef} className="relative">
            <div className="flex items-center gap-2 mb-2">
                <button
                    type="button"
                    onClick={requestSwap}
                    className="un-btn-ghost !px-2 !py-1 text-[0.65rem] inline-flex items-center gap-1"
                    data-testid={`word-swap-btn-${section}`}
                >
                    <Wand2 className="w-3 h-3" /> Word Swap (highlight first)
                </button>
            </div>
            <div className="un-script whitespace-pre-wrap" data-testid={`section-body-${section}`}>
                {currentText}
            </div>
            {open && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-un-onyx/70"
                    role="dialog"
                    aria-modal="true"
                    onClick={() => setOpen(false)}
                    data-testid={`word-swap-dialog-${section}`}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="un-card max-w-md w-full bg-white"
                    >
                        <div className="flex items-center justify-between border-b-2 border-un-onyx px-6 py-4">
                            <div className="un-label text-un-grape">Word Swap</div>
                            <button onClick={() => setOpen(false)} className="un-btn-ghost !px-1 !py-1" aria-label="Close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="text-xs text-un-onyx/60 mb-2">Replacing:</div>
                            <div className="un-mono text-sm font-bold bg-un-lime/60 border-2 border-un-onyx px-3 py-2 mb-4">
                                {phrase}
                            </div>
                            {loading ? (
                                <div className="flex items-center gap-2 text-un-onyx/70 py-6 justify-center">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Generating alternatives…
                                </div>
                            ) : options.length === 0 ? (
                                <div className="text-un-onyx/60 text-sm">No alternatives returned. Try a different phrase.</div>
                            ) : (
                                <div className="space-y-2">
                                    {options.map((o, i) => (
                                        <button
                                            key={i}
                                            onClick={() => applyReplacement(o)}
                                            className="w-full text-left border-2 border-un-onyx px-3 py-3 bg-white hover:bg-un-berry hover:text-white transition-colors"
                                            data-testid={`word-swap-option-${i}`}
                                        >
                                            <span className="text-sm leading-relaxed">{o}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
